import React, { useState } from 'react';
import { login } from '../api';

export default function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [kiosk, setKiosk] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError('');
    try {
      await login(password, kiosk);
      onLogin();
    } catch (err) {
      setError(err.message === 'Incorrect password'
        ? 'Incorrect password'
        : 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        {/* Logo / wordmark */}
        <div style={styles.logoRow}>
          <span style={styles.logoDecor}>◈</span>
          <span style={styles.logoText}>Smith Family</span>
          <span style={styles.logoDecor}>◈</span>
        </div>
        <p style={styles.subtitle}>Command Center</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputWrap}>
            <input
              type="password"
              placeholder="Family password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                ...styles.input,
                ...(error ? styles.inputError : {}),
              }}
              autoFocus
              autoComplete="current-password"
            />
          </div>

          {error && <p style={styles.errorMsg}>{error}</p>}

          <label style={styles.kioskLabel}>
            <input
              type="checkbox"
              checked={kiosk}
              onChange={e => setKiosk(e.target.checked)}
              style={styles.checkbox}
            />
            <span style={styles.kioskText}>This is the kitchen display — stay logged in permanently</span>
          </label>

          <button
            type="submit"
            disabled={loading || !password.trim()}
            style={{
              ...styles.submitBtn,
              ...(loading || !password.trim() ? styles.submitBtnDisabled : {}),
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>

      {/* Subtle background pattern */}
      <div style={styles.bgPattern} aria-hidden="true" />
    </div>
  );
}

const styles = {
  root: {
    height: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    position: 'relative',
    overflow: 'hidden',
  },
  bgPattern: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `radial-gradient(circle at 20% 30%, rgba(232,201,122,0.04) 0%, transparent 50%),
                      radial-gradient(circle at 80% 70%, rgba(122,184,232,0.04) 0%, transparent 50%)`,
    pointerEvents: 'none',
  },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    padding: '40px 36px 36px',
    width: '100%',
    maxWidth: 360,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logoDecor: {
    fontSize: '1rem',
    color: 'var(--accent)',
    opacity: 0.5,
  },
  logoText: {
    fontFamily: 'var(--font-mono)',
    fontSize: '1.4rem',
    color: 'var(--accent)',
    fontWeight: 500,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    lineHeight: 1,
    textShadow: '0 0 20px rgba(232,201,122,0.4)',
  },
  subtitle: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.6rem',
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: 'var(--text-dimmer)',
    marginBottom: 20,
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  inputWrap: {
    width: '100%',
  },
  input: {
    width: '100%',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text)',
    fontSize: '1rem',
    padding: '12px 14px',
    outline: 'none',
    fontFamily: 'var(--font-body)',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: 'var(--danger)',
  },
  errorMsg: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--danger)',
    textAlign: 'center',
    marginTop: -6,
  },
  kioskLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    cursor: 'pointer',
  },
  checkbox: {
    marginTop: 2,
    accentColor: 'var(--accent)',
    flexShrink: 0,
    width: 15,
    height: 15,
    cursor: 'pointer',
  },
  kioskText: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.7rem',
    color: 'var(--text-dim)',
    lineHeight: 1.5,
    letterSpacing: '0.02em',
  },
  submitBtn: {
    width: '100%',
    background: 'var(--accent)',
    color: '#0d0f14',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    padding: '13px',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.82rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'opacity 0.15s',
    marginTop: 4,
  },
  submitBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
};
